import { createContext, useContext } from "react"
import { resolveSpeakerColor } from "../lib/speaker-colors"

// Values are arbitrary CSS colour strings — palette hexes from the canonical
// map OR user-chosen override hexes, which are not one of the 12 palette
// literals. The map is therefore typed `string`, not `TSpeakerColor`.
const SpeakerColorsContext = createContext<Map<string, string> | null>(null)

export function SpeakerColorsProvider({
  colors,
  children,
}: {
  colors: Map<string, string>
  children: React.ReactNode
}): React.ReactElement {
  return <SpeakerColorsContext.Provider value={colors}>{children}</SpeakerColorsContext.Provider>
}

/**
 * Returns the assigned colour for a speaker name, falling back to a hash if
 * the context isn't mounted. Both the map lookup and the hash apply the same
 * `normalizeSpeakerKey` transform so "Mark", "Mark Smith", and "Mark
 * (Mark Smith)" resolve to one identity colour.
 */
export function useSpeakerColor(name: string): string {
  const map = useContext(SpeakerColorsContext)
  // Returns a CSS colour string: a palette hex from the canonical map, a
  // user-chosen override hex, or the deterministic hash fallback. The return
  // type is `string` (not `TSpeakerColor`) because override colours are arbitrary
  // user-chosen hexes, not necessarily one of the 12 palette literals.
  return resolveSpeakerColor(name, map ?? undefined)
}
